import { ToastController } from '@ionic/angular';
import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { TranslateConfigService } from './translate-config.service';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class ToastService {
  private renderer: Renderer2;
  private container: HTMLElement | null = null;


  constructor(
    public toastController: ToastController,
    private translateService: TranslateConfigService,
    private authService: AuthService,
    rendererFactory: RendererFactory2
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  async presentToast(message: string) {
    const toast = await this.toastController.create({
      position: 'bottom',
      duration: 2000,
      message: message,
      color: 'primary',
    });
    toast.present();
  }

  async presentToastWithReturn(message: string) {
    const toast = await this.toastController.create({
      position: 'bottom',
      message: message,
      buttons: [
        {
          text: 'OK',
          role: 'cancel',
          handler: () => {
            console.log('Cancel clicked');
          },
        },
      ]
    });
    toast.present();
    return toast;
  }

  filterExceptionMessage(message: string) {
    if (message.includes('@@@'))
      return message.split('@@@')[1].replace('@@@', '');

    return message;
  }

  async error(message: any) {
    // typeof, not constructor === Object/String: a raw network-level failure (no HTTP response
    // at all, e.g. the API is unreachable) surfaces as a ProgressEvent, and other call sites may
    // pass an Error instance — neither has `.constructor === Object`, so they used to fall into
    // the string branch below and crash on message.includes(), silently dropping the toast
    // (String() call inside an async method throws -> unhandled promise rejection, no UI at all).
    let isString = typeof message === 'string';
    let isObject = !isString && message !== null && typeof message === 'object';

    let errorParams: any;
    if (isObject) {
      if (message.title) message = message.title;
      else if (message.message) message = message.message;
      else message = JSON.stringify(message.errors ?? {});

      if (!message || message === '{}' || message === 'undefined') {
        message = this.translateService.getTranslation(
          'Sorry, we can NOT proceed with your request. Please check the following:'
        );
      }
    } else if (!message) {
      message = this.translateService.getTranslation(
        'Sorry, we can NOT proceed with your request. Please check the following:'
      );
    } else {
      message = this.filterExceptionMessage(message);
      if (message.includes('|ERROR_PARAMS')) {
        const msg = message.split('|ERROR_PARAMS')[0];
        const paramSplit = message.split('|ERROR_PARAMS')[1];
        errorParams = JSON.parse(paramSplit);
        message = this.translateService.getTranslation(msg);
      }
    }

    // const toast = await this.toastController.create({
    //   position: 'top',
    //   message: errorParams
    //     ? this.translateService.getTranslationWithParam(message, errorParams)
    //     : this.translateService.getTranslation(message),
    //   color: 'danger',
    //   header: this.translateService.getTranslation(
    //     'Sorry, we can NOT proceed with your request. Please check the following:'
    //   ),
    //   duration: 5000,
    //   buttons: [
    //     {
    //       text: 'OK',
    //       role: 'cancel',
    //       handler: () => {
    //         console.log('Cancel clicked');
    //       },
    //     },
    //   ],
    // });
    // toast.present();
    this.show(message, 5000, 'danger');
  }

  async success(message: string, duration: number = 2000, allowOK = false) {
    // const toast = await this.toastController.create({
    //   position: 'top',
    //   message: this.translateService.getTranslation(message),
    //   color: 'success',
    //   header: this.translateService.getTranslation('Success Information'),
    //   duration: duration,
    // });
    // toast.present();
    this.show(message, duration, 'success', allowOK);
  }

  async showForegroundNotification(
    title: string,
    message: string,
    duration: number = 3000
  ) {
    this.show(message, duration, 'primary', false, title);
  }

  async showLogoutHandle(
    title: string,
    message: string
  ) {
    const toast = await this.toastController.create({
      message: message,
      header: title,
      position: 'middle', // centers the toast vertically
      cssClass: 'confirm-toast',
      duration: 0,
      buttons: [
        {
          text: 'OK',
          role: 'cancel',
          handler: () => {
            this.authService.logout();
          },
        },
      ],
    });
    toast.present();
  }


  private ensureContainer() {
    if (!this.container) {
      this.container = this.renderer.createElement('div');
      this.renderer.setStyle(this.container, 'position', 'fixed');
      this.renderer.setStyle(this.container, 'top', '20px');
      this.renderer.setStyle(this.container, 'left', '50%');
      this.renderer.setStyle(this.container, 'transform', 'translateX(-50%)');
      this.renderer.setStyle(this.container, 'display', 'flex');
      this.renderer.setStyle(this.container, 'flexDirection', 'column');
      this.renderer.setStyle(this.container, 'gap', '10px');
      this.renderer.setStyle(this.container, 'zIndex', '50000');
      this.renderer.setStyle(this.container, 'width', '100%');
      this.renderer.setStyle(this.container, 'maxWidth', '500px');
      document.body.appendChild(this.container!);
    }
  }

  /**
   * Show a toast message
   * @param message Text to display
   * @param duration Auto-close duration in ms
   * @param color Color: primary, success, warning, danger
   * @param allowOk If true, adds an "OK" button to close manually
   * @param title Optional title/header for the toast
   */
  show(message: string, duration = 300000, color = 'primary', allowOk = false, title?: string) {
    this.ensureContainer();

    const toast = this.renderer.createElement('div');
    this.renderer.setStyle(toast, 'background', this.getColor(color));
    this.renderer.setStyle(toast, 'color', 'white');
    this.renderer.setStyle(toast, 'padding', '14px 16px');
    this.renderer.setStyle(toast, 'borderRadius', '6px');
    this.renderer.setStyle(toast, 'boxShadow', '0 2px 6px rgba(0,0,0,0.3)');
    this.renderer.setStyle(toast, 'opacity', '0');
    this.renderer.setStyle(toast, 'transition', 'opacity 0.3s, transform 0.3s');
    this.renderer.setStyle(toast, 'width', '90%');
    this.renderer.setStyle(toast, 'maxWidth', '500px');
    this.renderer.setStyle(toast, 'margin', '0 auto');
    this.renderer.setStyle(toast, 'display', 'flex');
    this.renderer.setStyle(toast, 'flexDirection', 'column');
    this.renderer.setStyle(toast, 'alignItems', 'stretch');

    // Add title if provided
    if (title) {
      const titleEl = this.renderer.createElement('div');
      titleEl.innerText = title;
      this.renderer.setStyle(titleEl, 'fontWeight', '600');
      this.renderer.setStyle(titleEl, 'marginBottom', '6px');
      this.renderer.setStyle(titleEl, 'fontSize', '15px');
      this.renderer.appendChild(toast, titleEl);
    }

    // Container for message + optional OK button
    const contentRow = this.renderer.createElement('div');
    this.renderer.setStyle(contentRow, 'display', 'flex');
    this.renderer.setStyle(contentRow, 'alignItems', 'center');
    this.renderer.setStyle(contentRow, 'gap', '10px');

    // Message (wraps correctly)
    const messageEl = this.renderer.createElement('span');
    messageEl.innerText = message;
    this.renderer.setStyle(messageEl, 'flex', '1');
    this.renderer.setStyle(messageEl, 'whiteSpace', 'normal');
    this.renderer.setStyle(messageEl, 'overflowWrap', 'break-word');
    this.renderer.setStyle(messageEl, 'word-break', 'break-word');
    this.renderer.setStyle(messageEl, 'lineHeight', '1.4');

    this.renderer.appendChild(contentRow, messageEl);

    // Optional OK button
    if (allowOk) {
      const btn = this.renderer.createElement('button');
      btn.innerText = 'OK';

      this.renderer.setStyle(btn, 'background', 'rgba(255,255,255,0.2)');
      this.renderer.setStyle(btn, 'border', 'none');
      this.renderer.setStyle(btn, 'color', 'white');
      this.renderer.setStyle(btn, 'padding', '4px 10px');
      this.renderer.setStyle(btn, 'borderRadius', '4px');
      this.renderer.setStyle(btn, 'cursor', 'pointer');
      this.renderer.setStyle(btn, 'flexShrink', '0'); // prevents being squished

      this.renderer.listen(btn, 'click', () => this.removeToast(toast));
      this.renderer.appendChild(contentRow, btn);
    }

    this.renderer.appendChild(toast, contentRow);
    this.renderer.appendChild(this.container, toast);
    // Animate fade-in
    setTimeout(() => {
      this.renderer.setStyle(toast, 'opacity', '1');
      this.renderer.setStyle(toast, 'transform', 'translateY(0)');
    }, 10);

    if(!allowOk)
    {
      // Auto-remove after duration
      if (duration > 0) {
        setTimeout(() => this.removeToast(toast), duration);
      }
    }
  }

  private removeToast(toast: HTMLElement) {
    this.renderer.setStyle(toast, 'opacity', '0');
    this.renderer.setStyle(toast, 'transform', 'translateY(-20px)');
    setTimeout(() => {
      if (this.container && toast.parentNode === this.container) {
        this.renderer.removeChild(this.container, toast);
      }
    }, 300);
  }

  private getColor(color: string) {
    switch (color) {
      case 'success': return '#28a745';
      case 'danger': return '#dc3545';
      case 'warning': return '#ffc107';
      case 'secondary': return '#6c757d';
      default: return '#3880ff';
    }
  }

}
